"use client";
// Imports
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  ToggleButton,
  ToggleExternalLinkButton,
  ToggleLinkButton,
  CommercialExhibitorDetails,
  ShowbagDetails,
  EventDetails,
  FoodFinderDetails,
  RideDetails,
} from "@/components";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { useIsLargeScreen, getValue, getPrice } from "@/lib/helpers";
import { useKindeSession } from "@/components/KindeWrapper";
import { useState, useEffect } from "react";

// View Details
export function ViewDetails({
  collectionHandle,
  data,
  showAddToMyShow,
  image,
  eventTimes,
  mapIsActive,
  instanceId, // New prop to uniquely identify each instance
}) {
  const { isLargeScreen } = useIsLargeScreen();
  const [isOpen, setIsOpen] = useState(false);

  // Check if this event should be auto-opened from URL
  useEffect(() => {
    const eventSlug = new URL(window.location.href).searchParams.get("event");
    if (eventSlug === data?.slug) {
      // Simple approach: check if this is the first instance of this slug in the DOM
      const allInstancesWithThisSlug = document.querySelectorAll(
        `[data-event-slug="${eventSlug}"]`
      );
      const firstInstance = allInstancesWithThisSlug[0];
      const thisInstance = document.querySelector(`[data-instance-id="${instanceId}"]`);

      // Only open if this instance is the first one found
      if (firstInstance === thisInstance) {
        setIsOpen(true);
      }
    }
  }, [data?.slug, instanceId]);

  // Update URL when opening/closing. Event drawers are client-only, like the
  // day filter, so this must not trigger a route render or CMS request.
  const handleOpenChange = (open) => {
    setIsOpen(open);
    const url = new URL(window.location.href);

    if (open) {
      url.searchParams.set("event", data?.slug);
    } else {
      url.searchParams.delete("event");
    }

    History.prototype.replaceState.call(window.history, {}, "", url);
  };

  if (data?.link_to_external_site)
    return (
      <ToggleExternalLinkButton
        url={data?.external_link}
        type={collectionHandle}
      />
    );

  if (data?.show_as_page)
    return <ToggleLinkButton url={data?.url} type={collectionHandle} />;

  if (isLargeScreen) {
    // Render
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <ToggleButton type={collectionHandle} variant="dialog" />
        <DialogContent
          className={`min-h-[500px] max-h-screen ${
            collectionHandle === "showbags"
              ? "[&>button]:bg-brand-red-gum [&>button>svg]:text-white"
              : ""
          }`}
        >
          <Details
            mapIsActive={mapIsActive}
            data={data}
            type={collectionHandle}
            showAddToMyShow={showAddToMyShow}
            image={image}
            eventTimes={eventTimes}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Render
  return (
    <Drawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      shouldScaleBackground={false}
      // The event query parameter changes without a page navigation, so Vaul
      // must restore the existing page position after opening.
      preventScrollRestoration={false}
    >
      <ToggleButton type={collectionHandle} variant="drawer" />
      <DrawerContent>
        <Details
          mapIsActive={mapIsActive}
          data={data}
          type={collectionHandle}
          showAddToMyShow={showAddToMyShow}
          image={image}
          eventTimes={eventTimes}
        />
      </DrawerContent>
    </Drawer>
  );
}

function Details({
  data,
  type,
  showAddToMyShow,
  image,
  eventTimes,
  mapIsActive,
}) {
  // Variables
  const { title, categories, items, showbag_stalls: stalls, locations } = data;
  const price = getPrice(type, data);
  const value = getValue(type, data);
  const { user } = useKindeSession();
  const userId = user?.id || null;

  // console.log("data", eventTimes);

  switch (type) {
    case "commercial_exhibitors":
      return (
        <CommercialExhibitorDetails
          mapIsActive={mapIsActive}
          userId={userId}
          data={data}
          type={type}
          title={title}
          content={data?.content}
          categories={data?.exhibitor_types}
          locations={locations}
          showAddToMyShow={showAddToMyShow}
          image={image}
        />
      );
    case "events":
      return (
        <EventDetails
          mapIsActive={mapIsActive}
          userId={userId}
          data={data}
          type={type}
          title={title}
          categories={categories}
          items={items}
          stalls={stalls}
          locations={locations}
          price={price}
          value={value}
          showAddToMyShow={showAddToMyShow}
          image={image}
          eventTimes={eventTimes}
        />
      );

    case "showbags":
      return (
        <ShowbagDetails
          mapIsActive={mapIsActive}
          userId={userId}
          data={data}
          type={type}
          title={title}
          operator={data?.showbag_operator?.title}
          categories={categories}
          items={items}
          stalls={stalls}
          locations={locations}
          price={price}
          value={value}
          showAddToMyShow={showAddToMyShow}
          image={image}
        />
      );

    case "food_outlets":
      return (
        <FoodFinderDetails
          mapIsActive={mapIsActive}
          userId={userId}
          data={data}
          type={type}
          foodType={data?.food_type?.title}
          title={title}
          categories={categories}
          locations={locations}
          showAddToMyShow={showAddToMyShow}
          image={image}
        />
      );

    case "rides":
      return (
        <RideDetails
          mapIsActive={mapIsActive}
          userId={userId}
          data={data}
          type={type}
          title={title}
          categories={categories}
          locations={locations}
          showAddToMyShow={showAddToMyShow}
          image={image}
        />
      );

    default:
      return null;
  }
}
